import { useEffect, useState, useCallback } from "react";
import { api, fmtDate, money } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import StatusBadge, { statusColor } from "@/components/StatusBadge";
import JobDetail, { SlaBadge } from "@/components/JobDetail";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, GripVertical, Inbox, AlertTriangle, Activity, Shuffle, CalendarDays, Columns3, AlarmClock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const WEEKLY_CAPACITY = 5;
const initials = (name) => (name || "").split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

function startOfWeek(d) {
  const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0, 0, 0, 0); return x;
}

export default function Schedule() {
  const [jobs, setJobs] = useState([]);
  const [techs, setTechs] = useState([]);
  const [emps, setEmps] = useState([]);
  const [mode, setMode] = useState("month");
  const [anchor, setAnchor] = useState(startOfWeek(new Date()));
  const [monthAnchor, setMonthAnchor] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [drag, setDrag] = useState(null);
  const [over, setOver] = useState(null);
  const [pending, setPending] = useState(null);
  const [slaConflict, setSlaConflict] = useState(null); // { j, body, msg, detail }
  const [detail, setDetail] = useState(null); // job popup
  const { company } = useTenant();
  const cur = company?.currency || "EUR";

  const load = useCallback(() => { api.get("/jobs").then((r) => setJobs(r.data)); }, []);
  useEffect(() => {
    load();
    api.get("/technicians").then((r) => setTechs(r.data));
    api.get("/employees").then((r) => setEmps(r.data)).catch(() => {});
  }, [load]);

  const iso = (d) => d.toISOString().slice(0, 10);
  const todayIso = iso(new Date());
  const days = [...Array(7)].map((_, i) => { const d = new Date(anchor); d.setDate(d.getDate() + i); return d; });

  const my = monthAnchor.getFullYear(), mm = monthAnchor.getMonth();
  const daysInMonth = new Date(my, mm + 1, 0).getDate();
  const firstDow = (new Date(my, mm, 1).getDay() + 6) % 7;
  const totalCells = Math.ceil((firstDow + daysInMonth) / 7) * 7;
  const gridStart = startOfWeek(new Date(my, mm, 1));
  const monthCells = [...Array(totalCells)].map((_, i) => { const d = new Date(gridStart); d.setDate(d.getDate() + i); return d; });

  const qualExpired = (name) => { const e = emps.find((x) => x.name === name); return e?.qualification_expiry && e.qualification_expiry.slice(0, 10) < todayIso; };
  const qualExpiring = (name) => {
    const e = emps.find((x) => x.name === name);
    if (!e?.qualification_expiry) return false;
    const exp = e.qualification_expiry.slice(0, 10);
    const soon = new Date(); soon.setDate(soon.getDate() + 14);
    return exp >= todayIso && exp <= soon.toISOString().slice(0, 10);
  };

  const DONE = ["Completed", "Closed", "Cancelled", "Completed on Site"];
  const unassigned = jobs.filter((j) => (!j.scheduled_date || !j.technician_id) && !DONE.includes(j.operational_status));
  const cellJobs = (techId, dayIso) => jobs.filter((j) => j.technician_id === techId && (j.scheduled_date || "").slice(0, 10) === dayIso).sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));
  const dayJobs = (dayIso) => jobs.filter((j) => (j.scheduled_date || "").slice(0, 10) === dayIso).sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));

  const weekIsoSet = days.map(iso);
  const weekLoad = (techId) => jobs.filter((j) => j.technician_id === techId && weekIsoSet.includes((j.scheduled_date || "").slice(0, 10)) && !DONE.includes(j.operational_status)).length;
  const loadStyle = (n) => n === 0 ? "bg-slate-100 text-slate-400" : n <= 3 ? "bg-emerald-100 text-emerald-700" : n <= 5 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";

  const reassign = async (job, techId, techName, dayIso) => {
    try {
      await api.put(`/jobs/${job.id}`, { technician_id: techId, technician_name: techName, scheduled_date: dayIso });
      toast.success(`${job.ref} moved to ${techName}`);
      load();
    } catch (e) { toast.error("Reassign failed"); }
  };

  const offerReassign = async (job, currentTechId, dayIso, warning) => {
    try {
      const { data: s } = await api.get(`/schedule/suggest-tech`, { params: { day: dayIso, exclude_id: currentTechId, service_type: job.service_type || job.job_type } });
      if (s.technician_id) {
        toast.warning(warning, {
          duration: 12000,
          action: { label: `Move to ${s.technician_name}${s.day_load === 0 ? " (free)" : ""}`, onClick: () => reassign(job, s.technician_id, s.technician_name, dayIso) },
        });
      } else { toast.warning(warning); }
    } catch (e) { toast.warning(warning); }
  };

  const rebalanceCell = async (techId, dayIso) => {
    const cj = cellJobs(techId, dayIso);
    if (cj.length < 2) return;
    const job = cj[cj.length - 1];
    try {
      const { data: s } = await api.get(`/schedule/suggest-tech`, { params: { day: dayIso, exclude_id: techId, service_type: job.service_type || job.job_type } });
      if (s.technician_id) { await reassign(job, s.technician_id, s.technician_name, dayIso); }
      else { toast.warning("No other technician available to rebalance"); }
    } catch (e) { toast.error("Rebalance failed"); }
  };

  const commitDrop = async (j, techId, techName, dayIso, override = false) => {
    const body = {
      scheduled_date: dayIso, technician_id: techId, technician_name: techName,
      operational_status: ["New", "Ready to Schedule", "Awaiting Review"].includes(j.operational_status) ? "Assigned" : j.operational_status,
    };
    if (override) body.sla_override = true;
    try {
      const { data } = await api.put(`/jobs/${j.id}`, body);
      toast.success(`${j.ref} → ${techName}, ${fmtDate(dayIso)}`);
      (data.warnings || []).forEach((w) => {
        if (w.includes("double-booking")) offerReassign(data, techId, dayIso, w);
        else toast.warning(w);
      });
      load();
    } catch (e) {
      if (e.response?.status === 409) setSlaConflict({ j, body, detail: e.response.data?.detail, retry: () => commitDrop(j, techId, techName, dayIso, true) });
      else toast.error("Reschedule failed");
    }
  };

  const drop = async (techId, techName, dayIso) => {
    setOver(null);
    const j = drag; setDrag(null);
    if (!j) return;
    if (j.technician_id === techId && (j.scheduled_date || "").slice(0, 10) === dayIso) return;
    const alreadyInWeek = j.technician_id === techId && weekIsoSet.includes((j.scheduled_date || "").slice(0, 10));
    const projected = weekLoad(techId) + (alreadyInWeek ? 0 : 1);
    if (projected > WEEKLY_CAPACITY) {
      setPending({ j, techId, techName, dayIso, projected });
      return;
    }
    commitDrop(j, techId, techName, dayIso);
  };

  const dropDay = async (dayIso, jArg = null, override = false) => {
    setOver(null);
    const j = jArg || drag; setDrag(null);
    if (!j) return;
    if ((j.scheduled_date || "").slice(0, 10) === dayIso) return;
    let opStatus = j.operational_status;
    if (["New", "Ready to Schedule", "Awaiting Review"].includes(opStatus)) opStatus = j.technician_id ? "Assigned" : "Scheduled";
    const body = { scheduled_date: dayIso, operational_status: opStatus };
    if (override) body.sla_override = true;
    try {
      const { data } = await api.put(`/jobs/${j.id}`, body);
      toast.success(`${j.ref} → ${fmtDate(dayIso)}`);
      (data.warnings || []).forEach((w) => { if (w.includes("double-booking") && j.technician_id) offerReassign(data, j.technician_id, dayIso, w); else toast.warning(w); });
      load();
    } catch (e) {
      if (e.response?.status === 409) setSlaConflict({ j, body, detail: e.response.data?.detail, retry: () => dropDay(dayIso, j, true) });
      else toast.error("Reschedule failed");
    }
  };

  const navPrev = () => { if (mode === "month") setMonthAnchor(new Date(my, mm - 1, 1)); else { const d = new Date(anchor); d.setDate(d.getDate() - 7); setAnchor(d); } };
  const navNext = () => { if (mode === "month") setMonthAnchor(new Date(my, mm + 1, 1)); else { const d = new Date(anchor); d.setDate(d.getDate() + 7); setAnchor(d); } };
  const navToday = () => { const n = new Date(); setAnchor(startOfWeek(n)); setMonthAnchor(new Date(n.getFullYear(), n.getMonth(), 1)); };
  const title = mode === "month" ? monthAnchor.toLocaleDateString(undefined, { month: "long", year: "numeric" }) : `Week of ${fmtDate(anchor)}`;

  const HoverInfo = ({ j }) => (
    <div className="space-y-1.5 max-w-[280px] py-0.5" data-testid={`sched-hover-${j.id}`}>
      <div className="font-semibold text-sm">{j.ref} · {j.title}</div>
      <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-xs">
        {[["Customer", j.customer_name], ["Site", j.site_name],
          ["When", `${fmtDate(j.scheduled_date)}${j.scheduled_time ? ` · ${j.scheduled_time}` : ""}`],
          ["Engineer", j.technician_name], ["Type", j.job_type],
          ["Value", j.expected_value ? money(j.expected_value, cur) : null],
          ["SLA deadline", j.sla_active && j.sla_deadline ? `${fmtDate(j.sla_deadline)}${j.sla_result ? ` — ${j.sla_result}` : ""}` : null],
        ].filter(([, v]) => v).map(([k, v]) => (
          <div key={k} className="contents"><span className="text-slate-400">{k}</span><span className="text-slate-100">{v}</span></div>
        ))}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap pt-0.5">
        <StatusBadge status={j.priority} /><StatusBadge status={j.operational_status} />
        {(j.flagged_notes || []).length > 0 && <span className="text-[10px] font-semibold bg-amber-400/20 text-amber-300 rounded px-1.5 py-0.5">{j.flagged_notes.length} flagged note{j.flagged_notes.length > 1 ? "s" : ""}</span>}
      </div>
      <div className="text-[10px] text-slate-400 border-t border-white/10 pt-1">Click for full details · drag to reschedule</div>
    </div>
  );

  const Card = (j) => (
    <Tooltip key={j.id}>
      <TooltipTrigger asChild>
        <div data-testid={`sched-card-${j.id}`} draggable
          onDragStart={() => setDrag(j)} onDragEnd={() => { setDrag(null); setOver(null); }}
          onClick={() => setDetail(j)}
          className="bg-white border border-slate-200 rounded p-1.5 text-xs cursor-pointer active:cursor-grabbing hover:shadow-md hover:border-slate-300 transition-all mb-1">
          <div className="flex items-center gap-1">
            <GripVertical className="h-3 w-3 text-slate-300 flex-shrink-0" />
            <span className="font-medium text-slate-800 truncate">{j.scheduled_time || ""} {j.ref}</span>
            {j.sla_active && <SlaBadge job={j} />}
          </div>
          <div className="text-slate-500 truncate pl-4">{j.customer_name}</div>
          <div className="pl-4 mt-0.5"><StatusBadge status={j.priority} /></div>
        </div>
      </TooltipTrigger>
      <TooltipContent side="right" className="bg-slate-900 text-white border-slate-700"><HoverInfo j={j} /></TooltipContent>
    </Tooltip>
  );

  const MonthChip = (j) => {
    const pc = statusColor(j.priority);
    return (
      <Tooltip key={j.id}>
        <TooltipTrigger asChild>
          <div data-testid={`month-chip-${j.id}`} draggable
            onDragStart={() => setDrag(j)} onDragEnd={() => { setDrag(null); setOver(null); }}
            onClick={() => setDetail(j)}
            className="flex items-center gap-1 rounded px-1 py-0.5 text-[10px] bg-slate-50 hover:bg-slate-100 border border-slate-100 cursor-pointer active:cursor-grabbing">
            <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: pc.dot }} />
            <span className="font-medium text-slate-700 truncate">{j.scheduled_time ? `${j.scheduled_time} ` : ""}{j.ref}</span>
            {j.sla_active && <span className="text-[8px] font-bold text-white bg-red-600 rounded px-0.5 flex-shrink-0">SLA</span>}
            {j.technician_name && <span className="ml-auto text-[9px] font-semibold text-white rounded-full h-3.5 w-3.5 grid place-items-center flex-shrink-0" style={{ backgroundColor: "var(--client-accent)" }}>{initials(j.technician_name)}</span>}
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="bg-slate-900 text-white border-slate-700"><HoverInfo j={j} /></TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider delayDuration={200}>
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div><h1 className="text-2xl font-head font-bold text-slate-900">Schedule</h1>
          <p className="text-sm text-slate-500">
            {mode === "month" ? "Drag jobs onto a day to reschedule · " : "Drag jobs onto a technician & day · "}{title}
          </p>
          {mode === "week" && (
            <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
              <span className="font-medium">Workload:</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-200 inline-block" />≤3</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-200 inline-block" />4–5</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-200 inline-block" />6+ overloaded</span>
            </div>
          )}</div>
        <div className="flex items-center gap-2">
          <Tabs value={mode} onValueChange={setMode}>
            <TabsList>
              <TabsTrigger value="month" data-testid="sched-view-month"><CalendarDays className="h-4 w-4 mr-1" />Month</TabsTrigger>
              <TabsTrigger value="week" data-testid="sched-view-week"><Columns3 className="h-4 w-4 mr-1" />Week</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button variant="outline" size="icon" data-testid="sched-prev" onClick={navPrev}><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" data-testid="sched-today" onClick={navToday}>Today</Button>
          <Button variant="outline" size="icon" data-testid="sched-next" onClick={navNext}><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {mode === "month" ? (
          <div className="lg:col-span-3 bg-white border border-slate-200 rounded-md p-2" data-testid="sched-month">
            <div className="grid grid-cols-7 gap-1 mb-1">
              {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                <div key={d} className="text-center text-xs font-medium text-slate-400 uppercase tracking-wide py-1">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {monthCells.map((d) => {
                const dIso = iso(d);
                const inMonth = d.getMonth() === mm;
                const isToday = dIso === todayIso;
                const dj = dayJobs(dIso);
                return (
                  <div key={dIso} data-testid={`month-cell-${dIso}`}
                    onDragOver={(e) => { e.preventDefault(); setOver(dIso); }}
                    onDragLeave={() => setOver((o) => (o === dIso ? null : o))}
                    onDrop={() => dropDay(dIso)}
                    className={`min-h-[108px] rounded-md border p-1 flex flex-col transition-colors ${over === dIso ? "ring-2 ring-inset accent-border bg-slate-50" : ""} ${inMonth ? "bg-white border-slate-100" : "bg-slate-50/60 border-transparent"}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-medium grid place-items-center h-5 min-w-5 px-1 rounded-full ${isToday ? "text-white" : inMonth ? "text-slate-700" : "text-slate-300"}`}
                        style={isToday ? { backgroundColor: "var(--client-accent)" } : {}}>{d.getDate()}</span>
                      {dj.length > 0 && <span className="text-[10px] text-slate-400">{dj.length}</span>}
                    </div>
                    <div className="space-y-0.5 overflow-y-auto scroll-thin flex-1">
                      {dj.slice(0, 4).map(MonthChip)}
                      {dj.length > 4 && <div className="text-[10px] text-slate-400 pl-1">+{dj.length - 4} more</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="lg:col-span-3 bg-white border border-slate-200 rounded-md overflow-x-auto scroll-thin">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left text-xs font-medium text-slate-500 px-3 py-2 sticky left-0 bg-slate-50 min-w-[140px]">Technician</th>
                  {days.map((d) => (
                    <th key={iso(d)} className={`text-xs font-medium px-2 py-2 min-w-[130px] ${iso(d) === todayIso ? "accent-text" : "text-slate-500"}`}>
                      {d.toLocaleDateString(undefined, { weekday: "short" })} {d.getDate()}/{d.getMonth() + 1}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {techs.map((t) => (
                  <tr key={t.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-sm font-medium text-slate-700 sticky left-0 bg-white align-top">
                      <span className="flex items-center gap-1.5">{t.name}
                        {qualExpired(t.name)
                          ? <AlertTriangle data-testid={`qual-expired-${t.id}`} className="h-3.5 w-3.5 text-red-500" title="Qualification expired" />
                          : qualExpiring(t.name) && <AlertTriangle data-testid={`qual-expiring-${t.id}`} className="h-3.5 w-3.5 text-amber-500" title="Qualification expires soon" />}</span>
                      <span data-testid={`workload-${t.id}`} className={`inline-flex items-center gap-1 mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${loadStyle(weekLoad(t.id))}`} title="Jobs this week">
                        <Activity className="h-3 w-3" />{weekLoad(t.id)} this week
                      </span>
                    </td>
                    {days.map((d) => {
                      const key = `${t.id}-${iso(d)}`;
                      const cj = cellJobs(t.id, iso(d));
                      const clash = cj.length > 1;
                      return (
                        <td key={key} data-testid={`sched-cell-${key}`}
                          onDragOver={(e) => { e.preventDefault(); setOver(key); }}
                          onDragLeave={() => setOver((o) => (o === key ? null : o))}
                          onDrop={() => drop(t.id, t.name, iso(d))}
                          className={`px-1.5 py-1.5 align-top border-l border-slate-100 ${over === key ? "bg-slate-100" : ""} ${clash ? "ring-1 ring-inset ring-amber-300 bg-amber-50/40" : ""}`}>
                          {clash && (
                            <div data-testid={`clash-${key}`} className="text-[10px] text-amber-700 flex items-center gap-1 mb-1">
                              <AlertTriangle className="h-3 w-3" />{cj.length} jobs
                              <button data-testid={`rebalance-${key}`} onClick={() => rebalanceCell(t.id, iso(d))}
                                className="ml-auto inline-flex items-center gap-0.5 text-[10px] accent-text hover:underline font-medium">
                                <Shuffle className="h-3 w-3" />Rebalance
                              </button>
                            </div>
                          )}
                          {cj.map(Card)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {techs.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-slate-400 text-sm">No technicians found.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        <div data-testid="sched-unassigned"
          onDragOver={(e) => { e.preventDefault(); setOver("unassigned"); }}
          onDragLeave={() => setOver((o) => (o === "unassigned" ? null : o))}
          className={`bg-white border rounded-md p-3 ${over === "unassigned" ? "border-dashed accent-border" : "border-slate-200"}`}>
          <h3 className="font-head font-semibold text-sm mb-2 flex items-center gap-1.5"><Inbox className="h-4 w-4" />Unassigned ({unassigned.length})</h3>
          <div className="space-y-1 max-h-[70vh] overflow-y-auto scroll-thin">
            {unassigned.map(Card)}
            {unassigned.length === 0 && <p className="text-xs text-slate-400">All jobs assigned to a technician & day.</p>}
          </div>
        </div>
      </div>

      <Dialog open={!!pending} onOpenChange={(v) => !v && setPending(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" />Technician overload</DialogTitle>
          </DialogHeader>
          {pending && (
            <p className="text-sm text-slate-600" data-testid="overload-message">
              Assigning <b>{pending.j.ref}</b> to <b>{pending.techName}</b> will give them <b>{pending.projected} jobs</b> this week — above the recommended limit of {WEEKLY_CAPACITY}. Continue anyway?
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="overload-cancel" onClick={() => setPending(null)}>Cancel</Button>
            <Button className="btn-accent" data-testid="overload-confirm" onClick={() => { const p = pending; setPending(null); commitDrop(p.j, p.techId, p.techName, p.dayIso); }}>Assign anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!slaConflict} onOpenChange={(v) => !v && setSlaConflict(null)}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><AlarmClock className="h-5 w-5 text-red-600" />SLA deadline warning</DialogTitle>
          </DialogHeader>
          {slaConflict && (
            <p className="text-sm text-slate-600" data-testid="sla-conflict-message">
              <b>{slaConflict.j.ref}</b>: {slaConflict.detail}
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" data-testid="sla-conflict-cancel" onClick={() => setSlaConflict(null)}>Pick another slot</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" data-testid="sla-conflict-override" onClick={() => { const c = slaConflict; setSlaConflict(null); c.retry(); }}>Schedule anyway (override)</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {detail && <JobDetail job={detail} onClose={() => setDetail(null)} onChanged={load} techs={techs} cur={cur} />}
    </div>
    </TooltipProvider>
  );
}
